import Paper from "@material-ui/core/Paper";
import React, { useEffect, useRef, useState,useMemo } from "react";
import styled from "styled-components";

import { Asset, Instrument } from "../../api/api";
import { theme } from "../../bootstrap/Theme";
import { useApi } from "../../hooks/useApi";
import { useNotify } from "../../hooks/system/useNotify";
import { useBoolean } from "../../hooks/utils/useBoolean";
import { useProfile } from "../../hooks/useProfile";
import { useMarketWidgets } from "../../hooks/useMarketWidgets";
import { useTradableAssets } from "../../hooks/useTradableAssets";
import { AuthAckResponse, AuthRequest, CancelOrderAckResponse, CancelOrderRejectResponse, ErrorMessageResponse, NewOrderAckResponse, NewOrderRejectResponse, Order, OrderBookSnapshotResponse, OrderExecutionResponse, PendingOrderAnnouncementResponse, PendingOrderUpdateResponse } from "../../trade/types";
import { TradeHeader } from "./components/TradeHeader";
import { MarketWidgetPanel } from "./widgets/markets/MarketWidgetPanel";
import { OrdersWidget } from "./widgets/orders/OrdersWidget";
import { DialogState } from "./widgets/orders/OrderDialog";

const websocketURL  = process.env.REACT_APP_WEBSOCKET_URL || "";

const Container     = styled.div`
  display         : flex;
  flex-direction  : column;
  justify-content : space-between;
  flex            : 1 0 auto;
`;

const BottomPanel   = styled(Paper)`
  height  : 30vh;
  margin  : ${(p) => theme(p).spacing(2, 0, 4)};
  padding : ${(p) => theme(p).spacing(1, 1)};
`;

export const TradingPage = () => {
  const { marketWidgets, loadMarketWidgets, setMarketWidgets }      = useMarketWidgets(),
        { profile }                                                 = useProfile(),
        marketPriceWebSocket                                        = useRef<WebSocket | null>(null),
        [ orders, setOrders ]                                       = useState<{ [orderID: string]: Order }>({}),
        [ orderBooks, setOrderBooks ]                               = useState<{ [instrumentID: number]: OrderBookSnapshotResponse }>({}),
        [ requestIDtoInstrumentID, setRequestIDtoInstrumentID ]     = useState<{ [requestID: string]: number }>({}),
        [ WSAuthorized, setWSAuthorized, unsetWSAuthorized ]        = useBoolean(),
        [ orderDialogState, setOrderDialogState ]                   = useState<DialogState>({isOpen: false, messages: [], title: ''}),
        [ updateEquity, setUpdateEquity ]                           = useState<Date>(),
        [ clientTradableInstruments, setClientTradableInstruments ] = useState<Instrument[]>([]),
        { api, isAuth }                                             = useApi(),
        { addNotify, handleApiError }                               = useNotify(),
        [ assetIdMap, setAssetIdMap ]                               = useState<{[assetId: number]: Asset}>({}),
        { tradableAssets }                                          = useTradableAssets(),
        [ instrumentIDToTicker, setInstrumentIDToTicker ]           = useState<{[instrumentID: number]: string}>({}),
        [ instrumentIDToQtyTicker, setInstrumentIDToQtyTicker ]     = useState<{[instrumentID: number]: string}>({}),
        tradeHeader                                                 = useMemo(() => <TradeHeader manualEquityReload={updateEquity}/>,[updateEquity]),
        ordersWidget                                                = useMemo(() => <OrdersWidget
                                                                                              assetIdMap={assetIdMap}
                                                                                              instrumentIDToTicker={instrumentIDToTicker}
                                                                                              marketPriceWebSocket={marketPriceWebSocket}
                                                                                              clientTradableInstruments={clientTradableInstruments}
                                                                                              orders={orders}
                                                                                              profile={profile}/>,[assetIdMap,instrumentIDToTicker,marketPriceWebSocket,clientTradableInstruments,orders,profile])

  useEffect( () => {
    if (profile) {
      marketPriceWebSocket.current = new WebSocket(websocketURL);

      marketPriceWebSocket.current.onopen = () => {
        console.log("Opening WebSocket");
        const authMessage: AuthRequest = {
          type  : "auth",
          jwt   : profile.jwtToken
        }
        marketPriceWebSocket.current?.send(JSON.stringify(authMessage));
      };

      marketPriceWebSocket.current.onerror = (event: Event) => {
        console.log("Websocket Error:", event);
      };

      marketPriceWebSocket.current.onclose = (event: CloseEvent) => {
        console.log("Websocket closed " + (event.wasClean ? "cleanly" : "abruptly") + " with code "  + event.code + (event.reason ? " because '" + event.reason + "'": ""));
      };

      return () => { console.log("Closing WebSocket"); marketPriceWebSocket.current?.close(); marketPriceWebSocket.current = null; unsetWSAuthorized(); }
    }
  }, [marketPriceWebSocket, profile, unsetWSAuthorized]);

  useEffect( () => {
    if (marketPriceWebSocket.current) {
      marketPriceWebSocket.current.onmessage = (e: MessageEvent) => {
        let response;
        try {
          response = JSON.parse(e.data);
        } catch (error) {
          console.log("Trouble parsing message event:", e);
          return
        }

        switch(response.type){
          case "auth_ack": {
            const authAck: AuthAckResponse = response;
            console.log("Websocket authorization acknowledged (" + authAck.client_id + ")");
            setWSAuthorized();
            break;
          }
          case "announce_order": {
            const pendingOrderAnnouncement: PendingOrderAnnouncementResponse = response;
            if (!orders[pendingOrderAnnouncement.order_id]) {
              let pendingOrder: Order = {
                initiation_time   : pendingOrderAnnouncement.initiation_time,
                update_time       : pendingOrderAnnouncement.update_time,
                expiration_time   : pendingOrderAnnouncement.expiration_time,
                instrument_id     : pendingOrderAnnouncement.instrument_id,
                order_id          : pendingOrderAnnouncement.order_id,
                initial_quantity  : pendingOrderAnnouncement.initial_quantity,
                side              : pendingOrderAnnouncement.side,
                limit             : pendingOrderAnnouncement.limit,
                filled_quantity   : pendingOrderAnnouncement.filled_quantity,
                filled_price      : pendingOrderAnnouncement.filled_price,
                status            : pendingOrderAnnouncement.status,
              };
              setOrders(previousState => {
                return {
                  ...previousState,
                  [pendingOrderAnnouncement.order_id]: pendingOrder
                }
              });
            }
            break;
          }
          case "book_snapshot": {
            const orderBook:            OrderBookSnapshotResponse = response,
                  responseInstrumentID: number                    = requestIDtoInstrumentID[orderBook.request_id];

            if (responseInstrumentID !== undefined) {
              setOrderBooks(prevOrderBooks => { return { ...prevOrderBooks, [responseInstrumentID]: orderBook } })
            }
            break;
          }
          case "cancel_order_ack": {
            const cancelOrderAck: CancelOrderAckResponse = response;
            setOrders(previousState => {
              const time_str  = new Date().toISOString().replace("T"," ").replace("Z","");
              return {
                ...previousState,
                [cancelOrderAck.order_id]: {
                  ...previousState[cancelOrderAck.order_id],
                  status      : (previousState[cancelOrderAck.order_id].filled_quantity === 0) ? "canceled" : "canceledpartial",
                  update_time : time_str
                }
              };
            });
            break;
          }
          case "cancel_order_reject": {
            const cancelOrderReject: CancelOrderRejectResponse = response;
            setOrderDialogState(previousState => {
              if (previousState.isOpen) {
                const messages: string[] = [...previousState.messages, cancelOrderReject.reject_reason]
                return {isOpen: true, messages: messages, title: "Cancel Order Rejected"}
              }
              return {isOpen: true, messages: [cancelOrderReject.reject_reason], title: "Cancel Order Rejected"}
            });
            break;
          }
          case "error_message": {
            const errorMessage: ErrorMessageResponse = response;
            console.log("Error: " + errorMessage.message)
            setUpdateEquity(new Date());
            break;
          }
          case "new_order_ack": {
            const newOrderAck:      NewOrderAckResponse = response,
                  order:            Order               = orders[newOrderAck.order_id];
            if( order ) {
              const newOrderMessage:  string = "New order placed for " + String(order.initial_quantity) + " " + instrumentIDToQtyTicker[order.instrument_id];
              setOrders(previousState => {
                const time_str  = new Date().toISOString().replace("T"," ").replace("Z","");
                return {
                  ...previousState,
                  [newOrderAck.order_id]: {
                    ...previousState[newOrderAck.order_id],
                    status        : "working",
                    update_time   : time_str
                  }
                };
              });
              setOrderDialogState(previousState => {
                if (previousState.isOpen) {
                  const messages: string[] = [...previousState.messages, newOrderMessage];
                  return {isOpen: true, messages: messages, title: "Order Placed"};
                }
                return {isOpen: true, messages: [newOrderMessage], title: "Order Placed"};
              });
            } else {
              console.log("Couldn't find order for "+newOrderAck.order_id);
              console.log(order);
            }
            break;
          }
          case "new_order_reject": {
            const newOrderReject: NewOrderRejectResponse = response;
            setOrders(previousState => {
              let newState: { [orderID: string]: Order } = {...previousState};
              delete newState[newOrderReject.order_id];
              return newState;
            });
            setOrderDialogState(previousState => {
              if (previousState.isOpen) {
                const messages: string[] = [...previousState.messages, newOrderReject.reject_reason];
                return {isOpen: true, messages: messages, title: "New Order Rejected"};
              }
              return {isOpen: true, messages: [newOrderReject.reject_reason], title: "New Order Rejected"};
            });
            break;
          }
          case "order_execution": {
            const orderExecution: OrderExecutionResponse = response;
            // const order: Order = orders[orderExecution.order_id]
            const message: string = "Order " + String(orderExecution.order_id) + " traded " + String(orderExecution.quantity) + " at " + String(orderExecution.price) + ", leaving " + String(orderExecution.leaves) + " remaining";
            setOrders(previousState => {
              return {
                ...previousState,
                [orderExecution.order_id]: {
                  ...previousState[orderExecution.order_id],
                  filled_quantity : previousState[orderExecution.order_id].filled_quantity + orderExecution.quantity,
                  filled_price    : orderExecution.price,
                  update_time     : orderExecution.execution_time,
                  status          : (orderExecution.leaves === 0) ? "partial" : "filled"
                }
              }
            });
            setOrderDialogState(previousState => {
              if (previousState.isOpen) {
                const messages: string[] = [...previousState.messages, message];
                return {isOpen: previousState.isOpen, messages: messages, title: "Execution Succeeded"}
              }
              return {isOpen: true, messages: [message], title: "Execution Succeeded"}
            });
            break;
          }
          case "pending_order_update": {
            const pendingOrderUpdate: PendingOrderUpdateResponse = response;
            if (orders[pendingOrderUpdate.order_id]) {
              setOrders(previousState => {
                return {
                  ...previousState,
                  [pendingOrderUpdate.order_id]: {
                    ...previousState[pendingOrderUpdate.order_id],
                    filled_quantity : pendingOrderUpdate.filled_quantity,
                    filled_price    : pendingOrderUpdate.filled_price,
                    update_time     : pendingOrderUpdate.update_time,
                    status          : pendingOrderUpdate.status
                  }
                }
              });
              setUpdateEquity(new Date());
            }
            break;
          }
          case "request_book_ack": {
            break;
          }
          case "request_book_cancel_ack": {
            break;
          }
          case "request_book_cancel_reject": {
            break;
          }
          case "request_book_reject": {
            break;
          }
          default: {
            console.log("Unknown websocket message type encountered:", response);
            setUpdateEquity(new Date());
            break;
          }
        }
      };
    }
  }, [orders, requestIDtoInstrumentID, setWSAuthorized, instrumentIDToTicker]);

  useEffect( () => {
    if (isAuth) {
      api.getClientTradingInstruments({})
        .then(handleApiError)
        .then(body => {
          setClientTradableInstruments(body ? body : new Array<Instrument>());
        })
        .catch(e => addNotify('error', 'Error', e.message))
    }
  }, [isAuth, api, handleApiError, setClientTradableInstruments, addNotify])

  useEffect( () => {
    if (tradableAssets !== undefined) {
      var newAssetIdMap: {[assetId: number]: Asset} = {};
      for (let i = 0; i < tradableAssets.length; i++) {
        newAssetIdMap[tradableAssets[i].id] = tradableAssets[i]
      }
      setAssetIdMap({...newAssetIdMap});
    }
  }, [tradableAssets, setAssetIdMap])

  useEffect( () => {
    if (clientTradableInstruments !== undefined && assetIdMap) {
      var newInstrumentIDToTicker:    {[instrumentID: number]: string} = {};
      var newInstrumentIDToQtyTicker: {[instrumentID: number]: string} = {};

      for (let i = 0; i < clientTradableInstruments.length; i++) {
          newInstrumentIDToTicker[clientTradableInstruments[i].instrumentId]      = ((clientTradableInstruments[i].ticker !== undefined && clientTradableInstruments[i].ticker !== "") ?
                                                                                        clientTradableInstruments[i].ticker! :
                                                                                        (clientTradableInstruments[i].contraAssetId !== undefined && clientTradableInstruments[i].contraAssetId !== 0) ?
                                                                                            (assetIdMap[clientTradableInstruments[i].assetId]?.ticker + '/' + assetIdMap[clientTradableInstruments[i].contraAssetId!]?.ticker) :
                                                                                            assetIdMap[clientTradableInstruments[i].assetId]?.ticker)
          newInstrumentIDToQtyTicker[clientTradableInstruments[i].instrumentId]   = (clientTradableInstruments[i].qtyInQuoteAsset !== undefined && clientTradableInstruments[i].qtyInQuoteAsset) ?
                                                                                        assetIdMap[clientTradableInstruments[i].quoteCurrencyId]?.ticker :
                                                                                        assetIdMap[clientTradableInstruments[i].assetId]?.ticker
      }
      setInstrumentIDToTicker({...newInstrumentIDToTicker});
      setInstrumentIDToQtyTicker( {...newInstrumentIDToQtyTicker});
    }
  }, [assetIdMap, clientTradableInstruments, setInstrumentIDToTicker, setInstrumentIDToQtyTicker])

  return (
    <Container>
      {tradeHeader}
      {marketPriceWebSocket.current && WSAuthorized &&
        <MarketWidgetPanel
          assetIdMap={assetIdMap}
          marketPriceWebSocket={marketPriceWebSocket}
          orderBooks={orderBooks}
          requestIDtoInstrumentID={requestIDtoInstrumentID}
          setRequestIDtoInstrumentID={setRequestIDtoInstrumentID}
          orderDialogState={orderDialogState}
          setOrderDialogState={setOrderDialogState}
          setOrders={setOrders}
          tradableAssets={tradableAssets}
          marketWidgets={marketWidgets}
          loadMarketWidgets={loadMarketWidgets}
          setMarketWidgets={setMarketWidgets}
          clientTradableInstruments={clientTradableInstruments}
        />}
      <BottomPanel>
        <h2 style={{margin: '0.5rem'}}>Orders</h2>
        {ordersWidget}
      </BottomPanel>
    </Container>
  );
};
